/* eslint-disable react/prop-types */
import ProductContext from 'context/product/productContext'
import AuthContext from 'context/auth/authContext'
import SubmitContext from 'context/submit/submitContext'

import PropTypes from 'prop-types'
import React, { useContext, useState, useRef } from 'react'
import { Button, Card, CardBody, Alert } from 'reactstrap'
import ProductDesc from 'views/shared/ProductDesc'
import ImagesUpload from 'views/shared/ImagesUpload'
import SamplePackage from 'views/shared/SamplePackage'

import {
  SUBMITTED_STATUS,
  IN_REVIEW_STATUS,
  REJECTED_STATUS,
  APPROVED_STATUS,
} from 'variables/categories'
import { ErrorMessage, FormContext, useForm } from 'react-hook-form'
import NotificationAlert from 'react-notification-alert'
import { useFormUtils } from 'views/shared/useFormUtils'

const ProductInfo = ({
  productDesc = <ProductDesc />,
  imagesUpload = <ImagesUpload />,
  nutritionInfo,
  packagingInfo = <SamplePackage />,
  submitterInfo,
  reviewStatus,
  comments,
  product,
  reviewOneFollowUp,
  reviewTwoFollowUp,
}) => {
  const [decision, setDecision] = useState()
  const productContext = useContext(ProductContext)
  const submitContext = useContext(SubmitContext)
  const authContext = useContext(AuthContext)

  const { checkImages } = useFormUtils()

  const { updateProduct, setSaving } = productContext
  const { updateSubmitterProduct, UPCE_to_UPCA, replaceProduct } = submitContext
  const { user } = authContext

  const methods = useForm()
  const { handleSubmit, setError } = methods

  const date = new Date()
  const currentDate =
    date.getFullYear() +
    '-' +
    ('0' + (date.getMonth() + 1)).slice(-2) +
    '-' +
    ('0' + date.getDate()).slice(-2) +
    'T' +
    ('0' + date.getHours()).slice(-2) +
    ':' +
    ('0' + date.getMinutes()).slice(-2) +
    ':' +
    ('0' + date.getSeconds()).slice(-2)
  const defaultDate = '1753-01-01T12:00:00'

  const notificationAlert = useRef()

  // save notifySave() implementation in case wants to reimplmement the feature in the future
  // eslint-disable-next-line no-unused-vars
  const notifySave = () => {
    let options = {
      place: 'bc',
      message: (
        <div>
          <h5>Your changes have been saved.</h5>
        </div>
      ),
      type: 'success',
      icon: 'nc-icon nc-bell-55',
      autoDismiss: 3,
    }
    notificationAlert.current.notificationAlert(options)
  }

  const onSubmit = (data, e) => {
    let errorRaised = false
    if (decision === 'approve' || decision === 'reject') {
      if (product.ReviewOneFollowUp) {
        errorRaised = true
        setError(
          'ReviewOneFollowUp',
          'followUpTrue',
          '⚠ Product is flagged. Remove flag before approving or rejecting.'
        )
      }
    }
    if (checkImages(product.images)) {
      if (decision === 'approve') {
        if (!product.ReviewerDescription) {
          errorRaised = true
          setError(
            'ReviewerDescription',
            'requiredOnApproval',
            '⚠ APL Description is required when approving a submission.'
          )
        }
        if (!product.ProductDesc) {
          errorRaised = true
          setError(
            'ProductDesc',
            'requiredOnApproval',
            '⚠ Product Description is required when approving a submission.'
          )
        }

        if (!product.ExchangeSize) {
          errorRaised = true
          setError(
            'ExchangeSize',
            'requiredOnApproval',
            '⚠ Exchange Size is required when approving a submission.'
          )
        }

        if (!product.UoM) {
          errorRaised = true
          setError(
            'UoM',
            'requiredOnApproval',
            '⚠ UOM is required when approving a submission.'
          )
        }

        if (
          product.fkApprovalStatus === IN_REVIEW_STATUS &&
          !product.EffectiveDate
        ) {
          errorRaised = true
          setError(
            'EffectiveDate',
            'requiredOnApproval',
            '⚠ You must enter an EBT Effective Date.'
          )
        }
        if (!errorRaised) {
          approveProduct(e)
        }
      } else if (decision === 'reject') {
        if (!product.Comments) {
          errorRaised = true
          setError(
            'Comments',
            'missingCommentOnRejection',
            '⚠ Reason/comment is required when rejecting a submission.'
          )
        }
        if (!errorRaised) {
          rejectProduct(e)
        }
      } else if (decision === 'save') {
        if (!errorRaised) {
          saveProduct(e)
        }
      }
    } else {
      setError(
        'ImagesUpload',
        'missingImage',
        '⚠ An image is required for submission.'
      )
    }
  }

  /* should duplicate attributes in switch cases */

  const rejectProduct = async e => {
    let currentProduct = {}
    e.preventDefault()
    console.log('product.fkApprovalStatus is: ' + product.fkApprovalStatus)
    switch (product.fkApprovalStatus) {
      case SUBMITTED_STATUS:
        currentProduct = {
          ...product,
          UPC:
            product.UPC.length === 8 ? UPCE_to_UPCA(product.UPC) : product.UPC,
          fkApprovalStatus: IN_REVIEW_STATUS,
          fkProduct: product.pkProduct,
          fkReviewerOne: user.pkContact,
          ReviewOneApproved: false,
          ReviewOneDate: currentDate,
          ReviewTwoApproved: false,
          ReviewTwoDate: defaultDate,
          Comments: product.Comments,
          ReviewerDescription: product.ReviewerDescription,
          RetailPrice:
            product.RetailPrice === ''
              ? null
              : (Math.round(product.RetailPrice * 100) / 100).toFixed(2),
          ExchangeSize:
            product.ExchangeSize === '' ? null : product.ExchangeSize,
          ReviewOneFollowUp: false,
          ReviewOneFollowUpReason: product?.ReviewOneFollowUpReason,
          ReviewTwoFollowUp: false,
          EffectiveDate: null,
        }
        console.log('currentProduct is: ' + JSON.stringify(currentProduct))
        if (currentProduct.UPC === currentProduct.oldUPC) {
          await updateProduct(currentProduct)
          await updateSubmitterProduct(currentProduct)
        } else {
          replaceProduct(currentProduct)
        }
        break
      case IN_REVIEW_STATUS:
        currentProduct = {
          ...product,
          UPC:
            product.UPC.length === 8 ? UPCE_to_UPCA(product.UPC) : product.UPC,
          fkApprovalStatus: REJECTED_STATUS,
          fkProduct: product.pkProduct,
          ReviewOneApproved: product.ReviewOneApproved === 'Approved',
          ReviewOneDate: product.ReviewOneDate,
          fkReviewerTwo: user.pkContact,
          ReviewTwoApproved: false,
          ReviewTwoDate: currentDate,
          Comments: product.Comments,
          ReviewerDescription: product.ReviewerDescription,
          RetailPrice:
            product.RetailPrice === ''
              ? null
              : (Math.round(product.RetailPrice * 100) / 100).toFixed(2),
          ExchangeSize:
            product.ExchangeSize === '' ? null : product.ExchangeSize,
          ReviewOneFollowUp: false,
          ReviewOneFollowUpReason: product?.ReviewOneFollowUpReason,
          ReviewTwoFollowUp: false,
          EffectiveDate: null,
        }
        console.log('currentProduct is: ' + JSON.stringify(currentProduct))
        if (currentProduct.UPC === currentProduct.oldUPC) {
          await updateProduct(currentProduct)
          await updateSubmitterProduct(currentProduct)
        } else {
          replaceProduct(currentProduct)
        }
        break
      default:
        break
    }
  }

  const approveProduct = async e => {
    console.log('user is: ' + JSON.stringify(user))
    let currentProduct = {}
    e.preventDefault()
    switch (product.fkApprovalStatus) {
      case SUBMITTED_STATUS:
        currentProduct = {
          ...product,
          UPC:
            product.UPC.length === 8 ? UPCE_to_UPCA(product.UPC) : product.UPC,
          fkApprovalStatus: IN_REVIEW_STATUS,
          fkProduct: product.pkProduct,
          fkReviewerOne: user.pkContact,
          ReviewOneApproved: true,
          ReviewOneDate: currentDate,
          ReviewTwoApproved: false,
          ReviewTwoDate: defaultDate,
          Comments: product.Comments,
          ReviewerDescription: product.ReviewerDescription,
          fkSubmitter: product.fkSubmitter,
          RetailPrice:
            product.RetailPrice === ''
              ? null
              : (Math.round(product.RetailPrice * 100) / 100).toFixed(2),
          ExchangeSize:
            product.ExchangeSize === '' ? null : product.ExchangeSize,
          ReviewOneFollowUp: false,
          ReviewOneFollowUpReason: product?.ReviewOneFollowUpReason,
          ReviewTwoFollowUp: !product.EffectiveDate
            ? product.ReviewTwoFollowUp
            : false,
          EffectiveDate:
            product.EffectiveDate === '' ? null : product.EffectiveDate,
        }
        console.log('currentProduct is: ' + JSON.stringify(currentProduct))
        if (currentProduct.UPC === currentProduct.oldUPC) {
          await updateProduct(currentProduct)
          await updateSubmitterProduct(currentProduct)
        } else {
          replaceProduct(currentProduct)
        }
        break
      case IN_REVIEW_STATUS:
        currentProduct = {
          ...product,
          UPC:
            product.UPC.length === 8 ? UPCE_to_UPCA(product.UPC) : product.UPC,
          fkApprovalStatus: APPROVED_STATUS,
          fkProduct: product.pkProduct,
          ReviewOneApproved: product.ReviewOneApproved === 'Approved',
          ReviewOneDate: product.ReviewOneDate,
          fkReviewerTwo: user.pkContact,
          ReviewTwoApproved: true,
          ReviewTwoDate: currentDate,
          Comments: product.Comments,
          ActiveDate: currentDate,
          ReviewerDescription: product.ReviewerDescription,
          fkSubmitter: product.fkSubmitter,
          RetailPrice:
            product.RetailPrice === ''
              ? null
              : (Math.round(product.RetailPrice * 100) / 100).toFixed(2),
          ExchangeSize:
            product.ExchangeSize === '' ? null : product.ExchangeSize,
          ReviewOneFollowUp: false,
          ReviewOneFollowUpReason: product?.ReviewOneFollowUpReason,
          ReviewTwoFollowUp: !product.EffectiveDate
            ? product.ReviewTwoFollowUp
            : false,
          EffectiveDate:
            product.EffectiveDate === '' ? null : product.EffectiveDate,
        }
        console.log('currentProduct is: ' + JSON.stringify(currentProduct))
        if (currentProduct.UPC === currentProduct.oldUPC) {
          await updateProduct(currentProduct)
          await updateSubmitterProduct(currentProduct)
        } else {
          replaceProduct(currentProduct)
        }
        break
      default:
        break
    }
  }

  const saveProduct = async e => {
    console.log('in saveProduct')
    e.preventDefault()
    const currentProduct = {
      ...product,
      UPC: product.UPC.length === 8 ? UPCE_to_UPCA(product.UPC) : product.UPC,
      fkApprovalStatus: product.fkApprovalStatus,
      fkProduct: product.pkProduct,
      fkReviewerOne: product.fkReviewerOne,
      fkReviewerTwo: product.fkReviewerTwo,
      ReviewOneApproved: product.ReviewOneApproved,
      ReviewOneDate: product.ReviewOneDate,
      ReviewTwoApproved: product.ReviewTwoApproved,
      ReviewTwoDate: product.ReviewTwoDate,
      Comments: product.Comments,
      ReviewerDescription: product.ReviewerDescription,
      fkSubmitter: product.fkSubmitter,
      RetailPrice:
        product.RetailPrice === ''
          ? null
          : (Math.round(product.RetailPrice * 100) / 100).toFixed(2),
      ExchangeSize: product.ExchangeSize === '' ? null : product.ExchangeSize,
      ReviewOneFollowUp: product.ReviewOneFollowUp,
      ReviewOneFollowUpReason: product?.ReviewOneFollowUpReason,
      ReviewTwoFollowUp: !product.EffectiveDate
        ? product.ReviewTwoFollowUp
        : false,
      EffectiveDate:
        product.EffectiveDate === '' ? null : product.EffectiveDate,
    }
    console.log('currentProduct is: ' + JSON.stringify(currentProduct))
    console.log('UPC is: ' + currentProduct.UPC)
    if (currentProduct.UPC === currentProduct.oldUPC) {
      await updateProduct(currentProduct, true)
      await updateSubmitterProduct(currentProduct)
    } else {
      replaceProduct(currentProduct)
    }
    // notifySave()
    setSaving(true)
  }

  const hrStyle = {
    border: '1px dashed #98ca63',
    borderRadius: '1px',
  }

  const cardStyle = {
    padding: '20px',
  }

  return (
    <FormContext {...methods}>
      <NotificationAlert ref={notificationAlert} />
      <form onSubmit={handleSubmit(onSubmit)} className={'Form'}>
        <div style={cardStyle}>
          <Card>
            <CardBody>
              <h6 className="text-info">Description</h6>
              {productDesc}
              {nutritionInfo && (
                <>
                  <hr style={hrStyle} />
                  <h6 className="text-info">Nutrition</h6>
                  {nutritionInfo}
                </>
              )}
              <hr style={hrStyle} />
              <h6 className="text-info">Packaging</h6>
              {packagingInfo}
              <hr style={hrStyle} />
              <h6 className="text-info">Images</h6>
              {imagesUpload}
              <p className={'text-danger text-center'}>
                <ErrorMessage
                  errors={methods.errors}
                  name="ImagesUpload"
                  as={<Alert color={'danger'} className={'mt-3'} />}
                />
              </p>
              <hr style={hrStyle} />
              <h6 className="text-info">Submitter Info</h6>
              {submitterInfo}
              <hr style={hrStyle} />
              <h6 className="text-info">Review Status</h6>
              {reviewStatus}
              <hr style={hrStyle} />
              <h6 className="text-info">Comments</h6>
              {comments}
              {reviewOneFollowUp}
              {reviewTwoFollowUp}
              {![3, 4].includes(product.fkApprovalStatus) && (
                <div>
                  <Button
                    type="submit"
                    className="btn-round float-right"
                    color="danger"
                    disabled={
                      product.fkReviewerOne === user.pkContact ||
                      [3, 4].includes(product.fkApprovalStatus)
                    }
                    onClick={e => {
                      setDecision('reject')
                    }}
                  >
                    Reject
                  </Button>
                  <Button
                    type="submit"
                    className="btn-round float-right"
                    color="success"
                    disabled={
                      product.fkReviewerOne === user.pkContact ||
                      [3, 4].includes(product.fkApprovalStatus)
                    }
                    onClick={e => {
                      setDecision('approve')
                    }}
                  >
                    Approve
                  </Button>
                  <Button
                    type="submit"
                    className="btn-round float-right"
                    color="primary"
                    disabled={
                      product.fkReviewerOne === user.pkContact ||
                      [3, 4].includes(product.fkApprovalStatus)
                    }
                    onClick={e => {
                      setDecision('save')
                    }}
                  >
                    Save
                  </Button>
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      </form>
    </FormContext>
  )
}

ProductInfo.propTypes = {
  productDesc: PropTypes.object,
  pricingInfo: PropTypes.object,
  imagesUpload: PropTypes.object,
  packagingInfo: PropTypes.object,
}

export default ProductInfo
